import emailSpellChecker from "@zootools/email-spell-checker"

// "Did you mean …@gmail.com?" on the sign-up email field (act:27d97c7e).
//
// THE INCIDENT. A claimant acquired through paid Meta spend typed
// `joshuaslawrence1983@gmil.com`, qualified, and signed a binding retainer 79
// seconds later. The firm has an executed agreement with a client it cannot
// email — and worse than that: `gmil.com` publishes a live mail exchanger and
// ACCEPTS mail, so the two emails sent to it, one carrying the signed retainer
// PDF, were most likely DELIVERED to an unrelated third party rather than
// bounced.
//
// That last fact is why this check exists at the door rather than downstream.
// A typosquat that accepts mail produces no hard bounce, therefore no Postmark
// webhook, no EmailSuppression, and nothing for a delivery-failure flag to fire
// on. Bounce detection is structurally blind to precisely the class of typo
// that is most likely — a one-letter miss of a major provider, because those
// are exactly the domains squatters register.
//
// ## Two constraints that are not negotiable
//
// IT SUGGESTS; IT NEVER REFUSES. This sits on the most conversion-sensitive
// field of a funnel the firm is actively buying traffic against, at roughly
// $40-50 per acquisition. A blocking validator that is wrong once costs a paid
// signup, and — the case that settles it — the person who typed the typo would
// be the one blocked. The visitor must always be able to submit what they
// typed. `QualForm.jsx`'s email validator does not reference this module at
// all, and a spec asserts that it does not.
//
// EVALUATE ON BLUR, NOT PER KEYSTROKE. `…@gmail.co` is one edit from
// `gmail.com`, so a per-keystroke check fires at every visitor halfway through
// typing a correct address and trains them to dismiss the thing. The caller
// owns that; this module is pure.
//
// ## Why a library, and why THIS one (act:27d97c7e's recorded decision)
//
// `.claude/rules/reach-for-the-stock-solution.md` applies: near-miss domain
// correction is a commodity problem and the distance function is the commodity
// part. Three candidates were checked against the live registry, and the
// leading one was then RUN against this lane's actual false-positive cases.
//
//   * `mailcheck` (the original) — rejected. Last published 2015, no ESM, and
//     it declares a global and probes window.jQuery at module scope.
//   * `email-misspelled` — rejected despite the best packaging. Whole-domain
//     distance only: it MISSES `icloud.cm`, and returns ranked multi-candidate
//     arrays, handing the pick — and the false-positive risk — back to us.
//   * `@zootools/email-spell-checker` — ADOPTED, pinned exact. Zero production
//     dependencies, MIT, and it carries separate TLD-level logic on top of its
//     domain list, which covers a whole class the domain-distance approach
//     misses.
//
// The decisive property is what it does NOT suggest. `ymail.com` and
// `mail.com` are each one edit from `gmail.com` and are REAL providers; a bare
// distance check would offer to "correct" them, which is this incident's own
// failure mode reversed — and one tap rather than a typo. The library ships
// them in its known-good list and stays silent, which is exactly why adopting
// the library beats adopting only its distance function.
//
// THE HONEST COST, recorded so it is not rediscovered: the npm artifact is
// frozen at 2023-01-18. The repository moved and carries TLD fixes that were
// never published, and no successor package exists on npm. This is adopted as a
// stable, zero-dependency lookup table, NOT as a maintained dependency, and it
// is pinned exact for that reason.
//
// The provider list is therefore the LIBRARY'S and stays JS-only — no Ruby
// reads it, so per `.claude/rules/cross-runtime-shared-vocab.md` there is no
// config file to single-source and no drift spec to write. Stated explicitly
// because the action asked for that call to be made either way.

// The package is CJS; under Vite's interop the callable lands on the default
// export, but a namespace import can surface it either way depending on the
// bundler. Resolve once, tolerantly, so a bundler change cannot silently turn
// the suggestion off.
const checker = emailSpellChecker?.run ? emailSpellChecker : emailSpellChecker?.default

/**
 * A suggested correction for a near-miss of a major mail provider.
 *
 * @param {string} email the address as typed
 * @returns {{full: string, domain: string} | null} null when there is nothing
 *   worth suggesting — an exact match on a known provider, an unrecognised
 *   domain (a work address, a small ISP), or anything too far away to be a
 *   confident typo.
 */
export function suggestEmailDomain(email) {
  const typed = typeof email === "string" ? email.trim() : ""
  // Cheap pre-checks before the library: an incomplete address is the normal
  // mid-typing state and must never produce a suggestion.
  if (!typed?.includes("@") || typed.endsWith("@")) return null

  let result
  try {
    result = checker?.run?.({ email: typed })
  } catch {
    // A suggestion is a nicety; a thrown error on the funnel's most valuable
    // field is not. Fail silent, never fail the form.
    return null
  }

  if (!result?.full || !result?.domain) return null
  // Defence in depth: never "correct" an address to itself.
  if (result.full.toLowerCase() === typed.toLowerCase()) return null

  return { full: result.full, domain: result.domain }
}

export default suggestEmailDomain
